import { z } from 'zod';

const decimalString = z
  .union([z.string(), z.number()])
  .transform((value) => String(value))
  .refine((value) => /^-?\d+(\.\d+)?$/.test(value), 'invalidDecimal');

export const RunValuationFormSchema = z.object({
  deal_id: z.string().trim().min(1, 'required'),
  valuation_date: z.string().min(1, 'required'),
  curve_set: z.string().trim().optional(),
  detailed: z.boolean(),
});

export type RunValuationForm = z.infer<typeof RunValuationFormSchema>;

export const ValuationResponseSchema = z.object({
  id: z.string(),
  deal_id: z.string(),
  valuation_date: z.string(),
  market_value: decimalString,
  clean_value: decimalString,
  accrued_interest: decimalString,
  book_value: decimalString,
  unrealized_pnl: decimalString,
  realized_pnl: decimalString,
  day_pnl: decimalString,
  currency: z.string(),
  calculated_at: z.string(),
  carry_pnl: decimalString,
  fx_pnl: decimalString,
  fx_rate: decimalString.nullable().optional(),
  produced_by: z
    .object({
      artifact_id: z.string().optional(),
      artifact_version: z.string().optional(),
      provider: z.string().optional(),
    })
    .nullable()
    .optional(),
});

export type ValuationResponse = z.infer<typeof ValuationResponseSchema>;

export const ValuationDetailSchema = z.object({
  id: z.string(),
  valuation_id: z.string(),
  valuation_date: z.string(),
  component_type: z.string(),
  component_ref: z.string(),
  label: z.string().nullable().optional(),
  notional: decimalString.nullable().optional(),
  present_value: decimalString,
  accrued_amount: decimalString.nullable().optional(),
  discount_factor: decimalString.nullable().optional(),
  forward_rate: decimalString.nullable().optional(),
  fixing_rate: decimalString.nullable().optional(),
  days_accrued: z.number().int().nullable().optional(),
  day_count_fraction: decimalString.nullable().optional(),
  currency: z.string(),
  calculation_details: z.record(z.string(), z.unknown()).nullable().optional(),
});

export type ValuationDetail = z.infer<typeof ValuationDetailSchema>;

// ---------------------------------------------------------------------------
// Valuation search — mirrors GET /api/v1/valuations.
// ---------------------------------------------------------------------------

export const ValuationSearchParamsSchema = z.object({
  valuation_type: z.string().optional(),
  valuation_date: z.string().optional(),
  currency: z.string().optional(),
  deal_id: z.string().optional(),
  limit: z.number().int().nonnegative().optional(),
  offset: z.number().int().nonnegative().optional(),
});

export type ValuationSearchParams = z.infer<typeof ValuationSearchParamsSchema>;

/** Lightweight list item returned by GET /api/v1/valuations. */
export const ValuationSummarySchema = z.object({
  id: z.string(),
  deal_id: z.string(),
  valuation_date: z.string(),
  valuation_type: z.string().nullable().optional(),
  currency: z.string(),
  market_value: decimalString,
  calculated_at: z.string(),
});

export type ValuationSummary = z.infer<typeof ValuationSummarySchema>;

export const ValuationSearchResponseSchema = z.object({
  items: z.array(ValuationSummarySchema),
  limit: z.number().int().optional(),
  offset: z.number().int().optional(),
  total: z.number().int().optional(),
});

export type ValuationSearchResponse = z.infer<typeof ValuationSearchResponseSchema>;
